import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDateWithWeekday, formatTime } from '../utils/dateUtils';
import { inviteTitle } from '../utils/inviteTitle';
import Button from './shared/Button';
import Icon from './shared/Icon';
import Modal from './shared/Modal';
import AdultAvatar from './shared/AdultAvatar';
import PhotoCredit from './shared/PhotoCredit';
import { resizeForSurface } from '../utils/imageSource';

/**
 * Unified modal for responding to invitations (activities and trips)
 * Supports Accept, Maybe, Decline with optional reply message
 */
function InvitationResponseModal({
  isOpen,
  onClose,
  type = 'activity', // 'activity' or 'trip'
  invitation,
  onRespond
}) {
  const { t } = useTranslation('modals');
  const [replyMessage, setReplyMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // One trip invitation can carry several decisions (a parent with two kids
  // invited, or their own invite plus a child's). Track what's been answered
  // so answered rows lose their buttons and the modal closes itself only when
  // nothing remains — the caller can't know that, it sees one response at a
  // time. Keyed per decision: child ids, or 'self'.
  const [answered, setAnswered] = useState({});

  // The component stays mounted between opens (isOpen prop), so reset the
  // per-invitation state whenever a different invitation arrives. Type is part
  // of the key: an activity and a trip can share an id.
  const invitationKey = invitation ? `${type}:${invitation.id}` : null;
  useEffect(() => {
    setAnswered({});
    setError(null);
    setIsSubmitting(false);
  }, [invitationKey]);

  // Which invitation is on screen NOW, for an answer that resolves later. The
  // modal can be closed mid-request and another invitation opened in the same
  // instance (Home's invite list, the bell's Invitations tab); without this the
  // late answer marked the new one answered, closed it, or showed its error.
  const currentKeyRef = useRef(invitationKey);
  currentKeyRef.current = invitationKey;

  if (!invitation) return null;

  const isActivity = type === 'activity';
  const typeLabel = isActivity ? t('invitationResponse.header.typeLabelActivity') : t('invitationResponse.header.typeLabelTrip');

  // formatDateWithWeekday and formatTime imported from dateUtils

  // childId is passed when a parent is answering for one specific child on an
  // invitation that covers several travelers. The modal closes itself once
  // every decision on the row is answered — and only then, so a parent can
  // work down the list without being cut off after the first response.
  const handleResponse = async (response, childId = null) => {
    const askedKey = invitationKey;
    const stillShowing = () => currentKeyRef.current === askedKey;
    setIsSubmitting(true);
    setError(null);
    try {
      await onRespond(response, (offersReply && replyMessage.trim()) || null, childId);
      // The answer landed for an invitation that's no longer open. It was
      // saved; the caller resyncs its lists. Nothing here belongs to it.
      if (!stillShowing()) return;

      const key = childId || 'self';
      const nextAnswered = { ...answered, [key]: response };
      setAnswered(nextAnswered);

      const remaining =
        (selfPending && !nextAnswered.self) ||
        childInvitees.some((c) => !nextAnswered[c.id]);
      if (!remaining) {
        setReplyMessage('');
        onClose();
      }
    } catch (err) {
      console.error('Error responding to invitation:', err);
      if (stillShowing()) setError(t('invitationResponse.errors.respondFailedFallback'));
    } finally {
      if (stillShowing()) setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReplyMessage('');
    setAnswered({});
    onClose();
  };

  // Build date/time info string. A trip on one day reads as that day, never
  // "Sat, Oct 10, 2026 - Sat, Oct 10, 2026" (C308); the words come from the
  // catalog (C304).
  const getDateTimeInfo = () => {
    if (isActivity) {
      const scheduledDate = invitation.scheduledDate || invitation.scheduled_date;
      const scheduledTime = invitation.scheduledTime || invitation.scheduled_time;
      const date = scheduledDate ? formatDateWithWeekday(scheduledDate) : null;
      const time = scheduledTime ? formatTime(scheduledTime) : null;
      if (date && time) return t('invitationResponse.when.dateAtTime', { date, time });
      if (date) return date;
      return null;
    } else {
      // Trip dates
      const startDate = invitation.startDate || invitation.start_date;
      const endDate = invitation.endDate || invitation.end_date;
      const oneDay = startDate && endDate && String(startDate).slice(0, 10) === String(endDate).slice(0, 10);
      const formattedStart = startDate ? formatDateWithWeekday(startDate) : null;
      const formattedEnd = endDate && !oneDay ? formatDateWithWeekday(endDate) : null;
      if (formattedStart && formattedEnd) return t('invitationResponse.when.dateRange', { start: formattedStart, end: formattedEnd });
      if (formattedStart) return formattedStart;
      return null;
    }
  };

  const dateTimeInfo = getDateTimeInfo();
  const location = invitation.location || invitation.custom_location || invitation.activity_location || invitation.destination;
  // A stop with no name anywhere is "An activity" (C327).
  const title = inviteTitle(
    invitation.title || invitation.activity_title || invitation.tripName || invitation.trip_name,
    { trip: !isActivity }
  );
  const fromName = invitation.fromName || invitation.from_user_name || invitation.organizerName;
  const fromAvatar = invitation.fromAvatar || invitation.from_user_avatar;
  // Only words someone wrote to the viewer: the invitation's own message. Never
  // the trip's description, which is the trip's, not a message (C308).
  const message = invitation.customMessage || invitation.custom_message || invitation.message;
  // Who the invitation asks about, resolved by the server: several travelers
  // behind one trip invitation, or the children named on an activity
  // invitation answered child by child (a reopened one, one carrying only
  // children). Absent, both default to the single-responder behaviour.
  // Each may say it can't be accepted (canAccept / selfCanAccept false): a
  // withheld invitation (C217) keeps its decline and never offers an accept
  // (C231). Nothing here says why.
  const canAcceptAll = invitation.canAccept !== false;
  const childInvitees = (invitation.childInvitees || []).map((c) => ({
    ...c,
    canAccept: canAcceptAll && c.canAccept !== false && c.can_accept !== false
  }));
  const selfPending = invitation.selfPending !== false;
  const selfCanAccept = canAcceptAll && invitation.selfCanAccept !== false;
  // A reopened activity invitation's answer reaches no one (no notice to the
  // sender, C242), so it offers no reply (C257). A child's Going answered
  // here reopens it too, for any sibling still waiting (the server's
  // answersInvitation rule), but the caller keeps the modal open on the
  // snapshot it was opened with — so count that accept here (CC-20-01).
  const acceptedHere = isActivity && Object.values(answered).includes('accepted');
  const offersReply = !invitation.reopened && !acceptedHere;
  // Anything on it may be accepted (else the copy doesn't talk about joining).
  // Who may: the line under the buttons names them (C309) — "you're added"
  // is wrong when only a child is.
  const selfMayAccept = selfPending && selfCanAccept;
  const childMayAccept = childInvitees.some((c) => c.canAccept);
  const offersAccept = selfMayAccept || childMayAccept;
  const tripInfoKey = selfMayAccept && childMayAccept
    ? 'invitationResponse.infoMessage.tripSelfAndChildren'
    : selfMayAccept
      ? 'invitationResponse.infoMessage.trip'
      : 'invitationResponse.infoMessage.tripChildren';
  // Optional preview extras — Home's Coming up passes them; the notification
  // center doesn't, and renders exactly as before.
  const coverPhoto = invitation.coverPhoto || null;
  const going = invitation.going || [];
  const goingCount = invitation.goingCount || 0;
  const planHref = invitation.planHref || null;
  // Its day gone by ('day' | 'trip', utils/pendingInvites inviteEnded; C291):
  // it can no longer be answered. What it was, said plainly — no reply box,
  // no buttons, and never "not found".
  const ended = invitation.ended === 'day' || invitation.ended === 'trip' ? invitation.ended : null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" ariaLabel={t('invitationResponse.header.ariaLabel', { type: typeLabel })}>
        <Modal.Header onClose={handleClose}>{t('invitationResponse.header.title', { type: typeLabel })}</Modal.Header>
        {coverPhoto && (
          /* Full modal width, so the full linked credit (Commons, Google,
             Tiqets; nothing for a user's photo). Bottom edge: the close button
             is in the header above, not on the photo. */
          <div className="relative">
            <img
              src={resizeForSurface(coverPhoto, 'medium')}
              alt=""
              className="w-full aspect-[16/9] object-cover"
            />
            <PhotoCredit src={resizeForSurface(coverPhoto, 'medium')} size="lg" />
          </div>
        )}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-error-light border border-error/20 rounded-lg" role="alert">
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {/* Invitation Details Card */}
          <div className="bg-primary-lighter rounded-lg p-4 mb-6 border border-primary-lighter">
            {/* Title */}
            <h4 className="font-semibold text-content-primary text-lg mb-3">{title}</h4>

            {/* Details */}
            <div className="space-y-2 text-sm text-content-secondary">
              {dateTimeInfo && (
                <div className="flex items-center space-x-2">
                  <Icon name="calendar" className="w-4 h-4" />
                  <span>{dateTimeInfo}</span>
                </div>
              )}

              {location && (
                <div className="flex items-center space-x-2">
                  <Icon name="mapPin" className="w-4 h-4" />
                  <span>{location}</span>
                </div>
              )}
            </div>

            {/* From User */}
            <div className="flex items-center space-x-3 mt-4 pt-4 border-t border-primary-lighter">
              {fromAvatar && (fromAvatar.startsWith('http') || fromAvatar.startsWith('data:') || fromAvatar.startsWith('/')) ? (
                <img
                  src={fromAvatar}
                  alt={fromName}
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-surface text-primary-dark flex items-center justify-center font-medium text-sm">
                  {fromAvatar || (fromName || t('invitationResponse.inviteCard.avatarInitialFallback')).charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <span className="text-sm text-content-secondary">{t('invitationResponse.inviteCard.invitedByLabel')}</span>
                <span className="text-sm font-medium text-content-primary">{fromName}</span>
              </div>
            </div>
          </div>

          {/* Who's already going — the thing most people want to know before
              answering a party or a group trip. Children count, never named. */}
          {goingCount > 0 && (
            <div className="mb-6">
              <h5 className="text-sm font-medium text-content-secondary mb-2">{t('invitationResponse.going.heading')}</h5>
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {going.map(person => (
                    <span key={person.id} className="rounded-full ring-2 ring-surface">
                      <AdultAvatar
                        name={person.name}
                        profilePicture={person.avatar && (person.avatar.startsWith('http') || person.avatar.startsWith('/')) ? person.avatar : null}
                        size="sm"
                      />
                    </span>
                  ))}
                </div>
                <span className="text-sm text-content-secondary">
                  {t('invitationResponse.going.count', { count: goingCount })}
                </span>
              </div>
            </div>
          )}

          {planHref && (
            <Link
              to={planHref}
              onClick={handleClose}
              className="mb-6 flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-surface text-sm font-medium text-primary hover:bg-surface-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2"
            >
              <span className="flex items-center gap-2">
                <Icon name="calendarDays" className="w-4 h-4" />
                {t('invitationResponse.seePlan')}
              </span>
              <Icon name="chevronRight" className="w-4 h-4" />
            </Link>
          )}

          {/* Sender's Message */}
          {message && (
            <div className="mb-6">
              <h5 className="text-sm font-medium text-content-secondary mb-2">{t('invitationResponse.messageSection.heading', { name: fromName })}</h5>
              <div className="bg-surface-tertiary rounded-lg p-3 text-content-secondary text-sm italic">
                {message}
              </div>
            </div>
          )}

          {/* Reply Message Input — only where a reply reaches someone */}
          {offersReply && !ended && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-content-secondary mb-2">
              {t('invitationResponse.replySection.label')}
            </label>
            <textarea
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              placeholder={t('invitationResponse.replySection.placeholder')}
              rows={3}
              disabled={isSubmitting}
              className="w-full p-3 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 resize-none bg-surface disabled:bg-surface-tertiary text-content-primary disabled:cursor-not-allowed"
            />
          </div>
          )}

          {/* Per-child responses. One invitation can cover several travelers —
              a parent with two kids on the same trip — and a single Accept
              can't say who it's for, so each child gets its own pair. */}
          {childInvitees.length > 0 && !ended && (
            <div className="mb-6 space-y-2">
              <p className="text-sm font-medium text-content-secondary">
                {t('invitationResponse.childSection.heading')}
              </p>
              {childInvitees.map((child) => (
                <div
                  key={child.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-surface"
                >
                  <span className="text-sm font-medium text-content-primary truncate">{child.name}</span>
                  {answered[child.id] ? (
                    // Answered — a second tap would only earn a 400 from the
                    // server, so the buttons give way to the outcome.
                    <span className={`text-xs font-semibold flex-shrink-0 ${
                      answered[child.id] === 'accepted' ? 'text-success' : 'text-content-muted'
                    }`}>
                      {answered[child.id] === 'accepted'
                        ? t('invitationResponse.childSection.accepted')
                        : t('invitationResponse.childSection.declined')}
                    </span>
                  ) : (
                  <div className="flex gap-2 flex-shrink-0">
                    {child.canAccept && (
                    <Button
                      variant="secondary"
                      onClick={() => handleResponse('accepted', child.id)}
                      disabled={isSubmitting}
                    >
                      {t('invitationResponse.footer.goingButton')}
                    </Button>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() => handleResponse('declined', child.id)}
                      disabled={isSubmitting}
                    >
                      {t('invitationResponse.footer.cantGoButton')}
                    </Button>
                  </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Once you've answered for yourself, show the outcome the same way
              answered child rows do — buttons silently vanishing reads as a
              bug, not a confirmation. Only reachable in the self+child case:
              a self-only invitation closes the modal on answer. */}
          {selfPending && answered.self && (
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-surface">
              <span className="text-sm font-medium text-content-primary">
                {t('invitationResponse.selfRowLabel')}
              </span>
              <span className={`text-xs font-semibold ${
                answered.self === 'accepted' ? 'text-success' : 'text-content-muted'
              }`}>
                {answered.self === 'accepted'
                  ? t('invitationResponse.childSection.accepted')
                  : t('invitationResponse.childSection.declined')}
              </span>
            </div>
          )}

          {/* Response Buttons — your own invitation. Hidden when this row is
              only about children (an unqualified Accept would be answering
              for someone never invited), and once you've answered. */}
          {selfPending && !answered.self && !ended && (
          <div className="flex gap-3">
            {/* Withheld (C217): the decline only, nothing says why (C231) */}
            {selfCanAccept && (
            <Button
              onClick={() => handleResponse('accepted')}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting
                ? t('invitationResponse.footer.sendingLabel')
                : isActivity ? t('invitationResponse.footer.goingButton') : t('invitationResponse.footer.joinTripButton')}
            </Button>
            )}
            {isActivity && selfCanAccept && (
              <Button
                variant="secondary"
                onClick={() => handleResponse('maybe')}
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? t('invitationResponse.footer.sendingLabel') : t('invitationResponse.footer.maybeButton')}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => handleResponse('declined')}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting
                ? t('invitationResponse.footer.sendingLabel')
                : isActivity ? t('invitationResponse.footer.cantGoButton') : t('invitationResponse.footer.notThisTimeButton')}
            </Button>
          </div>
          )}

          {/* Its day gone by (C291): said plainly, in place of every answer. */}
          {ended ? (
            <p role="status" className="p-3 rounded-lg bg-surface-secondary text-sm font-medium text-content-secondary">
              {t(`invitationResponse.ended.${ended}`)}
            </p>
          ) : (
          <div className="mt-4 p-3 bg-primary-lighter rounded-lg">
            <p className="text-xs text-primary">
              {!offersReply
                ? t('invitationResponse.infoMessage.activityReopened')
                : !offersAccept
                  ? t(isActivity ? 'invitationResponse.infoMessage.activityDeclineOnly' : 'invitationResponse.infoMessage.tripDeclineOnly')
                  : isActivity
                    ? t('invitationResponse.infoMessage.activity')
                    : t(tripInfoKey)
              }
            </p>
          </div>
          )}
        </div>
    </Modal>
  );
}

export default InvitationResponseModal;
